// src/features/system/iam-admin/api/iamAdminApi.ts
// ────────────────────────────────────────────────────────────────────────────
// Typed BFF client for the IAM membership admin surface.
//
// Endpoints map to /bff/iam/admin/* (Layer 3 digester, FE-ready shapes).
// Types mirror the BFF digestMembership / digestMembershipList output:
// camelCase, derived flags (isRetired, roleSummary), aggregated summary.
// ────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

// ── Domain types ─────────────────────────────────────────────────────────

export type MembershipStatus = 'active' | 'inactive' | 'locked';

export interface Membership {
  idMembership:       number;
  membershipRef:      string;
  idUser:             number;
  userRef:            string;
  tenantId:           number;
  tenantRef:          string;
  tenantName?:        string;
  username?:          string;
  roles:              string[];
  status:             MembershipStatus;
  isDefaultBusiness:  boolean;
  isRetired:          boolean;
  roleSummary:        string;
  validFrom?:         string | null;
  validTo?:           string | null;
  createdAt?:         string | null;
  updatedAt?:         string | null;
}

export interface MembershipSummary {
  total:        number;
  activeCount:  number;
  retiredCount: number;
  defaultCount: number;
}

export interface MembershipList {
  source:         'user' | 'tenant';
  includeRetired: boolean;
  memberships:    Membership[];
  summary:        MembershipSummary;
  serverTime:     string;
}

export interface UserPickerEntry {
  idUser:   number;
  userRef:  string;
  tenantId: number;
  username: string;
  fullName?: string | null;
  email?:    string | null;
  status?:   string | null;
}

export interface TenantPickerEntry {
  idTenant:   number;
  tenantRef:  string;
  tenantName: string;
  schema?:    string | null;
  isActive?:  boolean;
}

export interface AssignMembershipInput {
  tenant_ref:           string;
  roles?:               string[];
  status?:              MembershipStatus;
  is_default_business?: boolean;
}

export interface UpdateMembershipInput {
  roles?:               string[];
  status?:              MembershipStatus;
  is_default_business?: boolean;
}

// ── Query keys ───────────────────────────────────────────────────────────

export const IAM_ADMIN_KEYS = {
  users:           ['iam-admin', 'users'] as const,
  tenants:         ['iam-admin', 'tenants'] as const,
  userMemberships: (userRef: string, includeRetired: boolean) =>
    ['iam-admin', 'user-memberships', userRef, includeRetired] as const,
  tenantMembers: (tenantRef: string, includeRetired: boolean) =>
    ['iam-admin', 'tenant-members', tenantRef, includeRetired] as const,
};

// ── API calls ────────────────────────────────────────────────────────────

export async function listUsersForPicker(): Promise<UserPickerEntry[]> {
  const out = await bff.get<{ users: UserPickerEntry[] }>('/iam/admin/users');
  const rawList = Array.isArray((out as any)?.items)
    ? (out as any).items
    : Array.isArray(out?.users)
      ? out.users
      : Array.isArray(out)
        ? out
        : [];
  return rawList.map((u: any) => ({
    idUser:    Number(u.idUser ?? u.id_user) || 0,
    userRef:   String(u.userRef ?? u.user_ref ?? ''),
    tenantId:  Number(u.tenantId ?? u.tenant_id) || 0,
    username:  String(u.username ?? ''),
    fullName:   u.fullName ?? u.full_name ?? null,
    email:      u.email ?? null,
    status:     u.status ?? null,
  }));
}

export async function listTenantsForPicker(): Promise<TenantPickerEntry[]> {
  const out = await bff.get<{ tenants: TenantPickerEntry[] }>('/iam/admin/tenants');
  const rawList = Array.isArray(out?.tenants) ? out.tenants : Array.isArray(out as any) ? (out as any) : [];
  return rawList.map((t: any) => ({
    idTenant:   Number(t.idTenant ?? t.id_tenant) || 0,
    tenantRef:  String(t.tenantRef ?? t.tenant_ref ?? ''),
    tenantName: String(t.tenantName ?? t.tenant_name ?? ''),
    schema:     t.schema ?? t.tenant_schema ?? null,
    isActive:   t.isActive ?? t.is_active,
  }));
}

export async function fetchUserMemberships(
  userRef: string,
  includeRetired = false,
): Promise<MembershipList> {
  const qs = includeRetired ? { include_retired: 'true' } : undefined;
  return bff.get<MembershipList>(`/iam/admin/users/${userRef}/memberships`, qs);
}

export async function fetchTenantMembers(
  tenantRef: string,
  includeRetired = false,
): Promise<MembershipList> {
  const qs = includeRetired ? { include_retired: 'true' } : undefined;
  return bff.get<MembershipList>(`/iam/admin/tenants/${tenantRef}/members`, qs);
}

export async function assignMembership(
  userRef: string,
  input: AssignMembershipInput,
): Promise<Membership> {
  return bff.post<Membership, AssignMembershipInput>(
    `/iam/admin/users/${userRef}/memberships`, input);
}

export async function updateMembership(
  membershipRef: string,
  input: UpdateMembershipInput,
): Promise<Membership> {
  return bff.patch<Membership, UpdateMembershipInput>(
    `/iam/admin/memberships/${membershipRef}`, input);
}

export async function retireMembership(membershipRef: string): Promise<void> {
  await bff.del<void>(`/iam/admin/memberships/${membershipRef}`);
}
